import React, {useCallback, useState} from 'react';
import {useParams} from 'react-router';
import {Alert, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {PlayArrow} from '@mui/icons-material';
import {useReportStore} from '../../../../useReportStore';
import {findStatements} from '../../../../../../utils/api/referenceAnalysis';

/**
 * Actions component for Reference Analysis - provides the "Find Statements" button
 */
const ReferenceAnalysisActions = ({onStatementsFound}) => {
    const {reportId} = useParams();
    const editorView = useReportStore(s => s.editorView);

    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState(null);

    /**
     * Get document content from editor
     */
    const getDocumentContent = useCallback(() => {
        if (!editorView) return null;

        // If it has a TipTap editor reference
        if (editorView.editor) {
            return editorView.editor.getJSON();
        }

        // Otherwise work directly with the ProseMirror view
        return editorView.state?.doc?.toJSON();
    }, [editorView]);

    /**
     * Handle analyze button click
     */
    const handleAnalyze = useCallback(async () => {
        if (!reportId || isAnalyzing) return;

        const content = getDocumentContent();
        if (!content) {
            setError('Editor not available or could not get document content');
            return;
        }

        setIsAnalyzing(true);
        setError(null);
        setResults(null);

        try {
            console.log('🎯 Starting statements analysis...');
            console.log('Document content:', content);

            // Call the API endpoint with only document content
            const result = await findStatements(content);

            console.log('✅ Statements analysis complete:', result);

            // Pass statements to parent component
            if (result?.statements && Array.isArray(result.statements)) {
                onStatementsFound?.(result.statements);
                setResults({message: `Found ${result.statements.length} statements requiring citations`});
            } else {
                onStatementsFound?.([]);
                setResults({message: 'No statements requiring citations found'});
            }

        } catch (err) {
            console.error('❌ Error analyzing statements:', err);
            setError(err.message || 'Failed to analyze statements');
        } finally {
            setIsAnalyzing(false);
        }
    }, [reportId, isAnalyzing, getDocumentContent, onStatementsFound]);

    return (
        <Stack p={2} spacing={2} borderBottom={1} borderColor="divider">
            <Typography variant="body2" color="text.secondary">
                Analyze your report to identify statements that require proofs or citations.
            </Typography>

            <Button
                variant="contained"
                startIcon={isAnalyzing ? <CircularProgress size={20}/> : <PlayArrow/>}
                onClick={handleAnalyze}
                disabled={isAnalyzing || !editorView}
                fullWidth
            >
                {isAnalyzing ? 'Analyzing...' : 'Find Statements Requiring Proofs'}
            </Button>

            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {results && (
                <Alert severity="success" onClose={() => setResults(null)}>
                    {results.message}
                </Alert>
            )}
        </Stack>
    );
};

export default ReferenceAnalysisActions;
